/**
 * @module: 简历相关
 * @author: xiongsha
 * @date: 2019-09-27 18:39:00
 */

import { observable, action, flow } from 'mobx';
import axios from 'axios';
import { Toast } from 'antd-mobile';
import { ajax } from '@clouderwork/ywk';
import { IResumeItem, IResumeDetail, IEmployments, IPortfolios, ICandidateList, IZhiwei } from '@src/entity/zhaopin/resume'
import { withRole } from '@src/utils/residentNew';
import { resumeStatusConfig } from '@src/utils/mapping';
interface IResumeParams {
  pagenum: number // 页码
  pagesize: number // 每页条数
  sort: string // 排序规则 refresh_at表示按照更新时间排序
  keyword: string //关键字
  hide_info: number //是否把重要信息打码，需要打码就传2
  recommend?: number // 是否为推荐简历（需要过滤为是的时候值为1）
  // expect_job?: string //职位名称
  tech_id?: number // 三级职能id
  tech_id_level_two?: number // 二级职能id
}
// 投递映射
// 100	筛选阶段
// 120	笔试阶段
// 150	面试阶段
// 600	offer阶段 待录用
// 610	offer阶段 待确认
// 620	待入职阶段
// 650	已入职阶段
// 900	失败/放弃阶段
// 1000	失败/放弃阶段

interface ICandidateParams {
  // 候选人接口类型定义
  pagenum: number // 页码
  pagesize: number // 每页条数
  exper_year?: number // 工作年限
  degree?: number //学历过滤
  apply_at?: number // 应聘时间
  keyword?: string // 关键字
  offer_uuid?: string //职位uuid
  status?: string // 投递状态（详见投递状态映射）
}

class Resume {
  // 简历列表数组
  @observable resumes: IResumeItem[] = [];
  // 接口请求状态
  @observable sendState: string = 'init';
  // 简历详情
  @observable resumeDetail?: IResumeDetail
  // 我的简历详情
  @observable mineDetail?: IResumeDetail
  // 招人才工作经历数组
  @observable employments?: IEmployments
  // 招人才 项目经历数组
  @observable portfolios?: IPortfolios
  // 候选人学历要求Id
  @observable eduExperId: number | null = null;
  // 候选人工作经验Id
  @observable workExperId: number | null = null;
  // 候选人应聘时间Id
  @observable applyId: number | null = null;
  // 我的候选人列表
  @observable candidateList: ICandidateList[] = []
  // 我的候选人筛选弹窗
  @observable candidateModal: boolean = false
  // h5中下载App弹窗
  @observable uploadModal: boolean = false
  // 职位切换弹窗
  @observable toggleModal: boolean = false
  @observable offers: IZhiwei[] = []
  // 职能id(对应二级职能id)
  @observable techId2: number | undefined;
  // 职位名字
  @observable zhiweiName: string = ''
  // 职位uuid
  @observable zhiweiUuid: string = ''
  @observable pullUp: boolean = false
  // 招人才tab值
  @observable resumesTab: string = 't1'

  @observable candidateFilter: {
    eduExperId: number | null;
    workExperId: number | null;
    applyId: number | null;
  } = {
      eduExperId: null,
      workExperId: null,
      applyId: null
    }

  // 用于cancel ajax请求
  @observable source;

  @action.bound
  changeModal = (name: string, val: boolean) => {
    // 修改弹窗显示隐藏
    this[name] = val
  }
  @action.bound
  setResumesTab = (val: string) => {
    // 招人才tab值切换
    this.resumesTab = val
  }
  @action.bound
  setSendState = (sendState: string) => {
    // 设置接口请求状态
    this.sendState = sendState
  }
  // 获取简历列表
  @action.bound
  getResumeList = flow(function* (this: Resume, params: IResumeParams, isPullOrPush: boolean = false) {
    if (!isPullOrPush) {
      // 获取简历详情
      this.sendState = 'pending';
    }
    try {
      const res: any = yield ajax.getJson('/zhaopin/api/resumes', withRole(params))
      this.sendState = 'done'
      if (res.error_code === 0) {
        const data = res.resumes
        if (res.total < (params.pagenum * 10)) {
          // 不可以继续翻页
          this.pullUp = true
        } else {
          this.pullUp = false
        }
        if (params.pagenum === 1) {
          this.resumes = data
          return Promise.reject('刷新成功')
        } else {
          this.resumes = this.resumes.concat(data)
        }
      } else {
        return Promise.reject('请求错误')
      }
    } catch (error) {
      this.sendState = 'error'
      return Promise.reject('请求错误')
    }
  })

  /**
   * @param hide_info 1 显示 2 隐藏
   */
  @action.bound
  getResumeDetail = flow(function* (this: Resume, uuid: string, hide_info = 2, delivery_uuid?: string) {
    // 获取简历详情
    this.sendState = 'pending'
    const params: any = {
      hide_info,
    }
    if(delivery_uuid) {
      params.delivery_uuid = delivery_uuid;
    }
    try {
      console.log('/api/resumes/detail====', 222);
      const res: any = yield ajax.getJson(`/zhaopin/api/resumes/detail/${uuid}`, withRole(params))
      this.sendState = 'done'
      if (res.error_code === 0) {
        this.resumeDetail = res.resume
      } else {
        Toast.fail(res.message, 1);
      }
    } catch (error) {
      this.sendState = 'error'
    }
  })

  @action.bound
  getMineDetail = flow(function* (this: Resume) {
    // 获取我的简历详情
    this.sendState = 'pending'
    try {
      const res: any = yield ajax.getJson('/zhaopin/api/resumes/mine', withRole({}, 2))
      this.sendState = 'done'
      if (res.error_code === 0) {
        this.mineDetail = res.resume
      }
    } catch (error) {
      this.sendState = 'error'
    }
  })

  @action.bound
  getOfferName = flow(function* (this: Resume, name: string, autoSetZhiwei?: boolean) {
    // 获取职位
    try {
      const res: any = yield ajax.getJson('/zhaopin/api/delivery/offers', withRole({
        name: name,
        status: 1,
        order_by: 'create_at',
        pagenum: 1,
        pagesize: 999
      }))
      if (res.error_code === 0) {
        const offers = res.offers
        this.offers = res.offers
        if (autoSetZhiwei && (!!!this.techId2) && (this.zhiweiUuid === '') && (this.zhiweiName === '')) {
          this.techId2 = (offers[0] && offers[0].tech_id_level_two) || this.techId2
          this.zhiweiUuid = (offers[0] && offers[0].uuid) || this.zhiweiUuid
          this.zhiweiName = (offers[0] && offers[0].name) || this.zhiweiName
        } else if(this.zhiweiUuid && !this.zhiweiName) {
          const findedOffer = this.offers.find(offer => offer.uuid === this.zhiweiUuid);
          if(findedOffer) {
            this.zhiweiName = findedOffer.name;
          }
        }
      } else {
        Toast.fail(res.message, 1);
      }
    } catch (error) {
      console.log(error);
    }
  })

  @action.bound
  setPortfolios = (portfolios: IPortfolios) => {
    // 存储项目经历
    this.portfolios = portfolios
  }

  @action.bound
  setEmployments = (employments: IEmployments) => {
    // 存储工作经历
    this.employments = employments
  }

  @action.bound
  setExper = (id: number, name: string) => {
    // 如果里面该id存在教育经历中这从教育经历中移除该ID，否则则往教育经历中加入该ID
    this[name] = id
  }

  /**
   * @param {boolean} isPull 是否上拉加载
   */
  @action.bound
  getCandidateList = flow(function* (this: Resume, params: ICandidateParams, isPullOrPush: boolean = false) {
    if (!isPullOrPush) {
      this.sendState = 'pending';
    }

    // 获取候选人列表
    try {
      // 接口列表文档http://39.107.108.1:9098/resident/docs/delivery/delivery/#3
      const res:any = yield ajax.getJson('/zhaopin/api/delivery/offers/resumes', withRole(params))
      this.sendState = 'done'
      if (res.error_code === 0) {
        const { status } = params; // 投递状态
        const data = res.delivery.map((cur) => {
          let value = '';
          if (cur.status) {
            const keys = Object.keys(resumeStatusConfig);
            let key = keys.find(key => key.includes(cur.status.toString()));
            if (key) value = resumeStatusConfig[key].value;
          }
          const resume = cur.resume
          return {
            ...cur,
            avatar: resume.avatar,
            real_name: resume.real_name,
            gender: resume.gender_text,
            work_city: resume.work_city,
            start_work_text: resume.start_work_text,
            exper_year_text: resume.exper_year_text,
            age: resume.age,
            salary: resume.salary,
            skills: resume.skills,
            last_edu: resume.last_edu,
            job_status: resume.job_status,
            uuid: resume.uuid,
            deliverUuid: cur.uuid,
            deliverOffer: cur.offer,
            status: status ? resumeStatusConfig[status].value : value, // 候选人statusNum阶段映射
            statusNum: cur.status // 候选人statusNum
          }
        })
        if (res.total < (params.pagenum * 10)) {
          // 不可以继续翻页
          this.pullUp = true
        } else {
          this.pullUp = false
        }
        if (params.pagenum === 1) {
          this.candidateList = data
          return Promise.reject('刷新成功')
        } else {
          this.candidateList = this.candidateList.concat(data)
        }
      } else {
        return Promise.reject("请求失败")
      }
    } catch {
      this.sendState = 'error'
      return Promise.reject("请求失败")
    }
  })

  @action.bound
  setCandidateModal = (val: boolean) => {
    // 控制候选人筛选弹窗显示隐藏
    this.candidateModal = val
  }

  @action.bound
  setToggleModal = (val: boolean) => {
    // 控制职位切换弹窗显示隐藏
    this.toggleModal = val
  }

  @action.bound
  setZhiweiName = (val: string, uuid: string, techId2: number | undefined) => {
    // 存储选中的值
    this.zhiweiName = val
    this.zhiweiUuid = uuid
    this.techId2 = techId2
  }
}

export default Resume
